import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KatanaTranslateToWatchlistResponse,
} from '@/types';
import { ReusableModal } from '../../modals/ReusableModal';
import {
  useCreateWatchlistMutation,
  useGetWatchlistsQuery,
  useTranslateKatanaPathsToWatchlistMutation,
} from '../../services/edgeApi';

type ExportToPathReportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  hasAppliedFilters: boolean;
  sourcePathIds: number[];
};

const toErrorMessage = (value: unknown): string | null => {
  if (typeof value === 'string') return value;

  if (Array.isArray(value)) {
    const parts = value
      .map((item) => toErrorMessage(item))
      .filter((item): item is string => Boolean(item));
    return parts.length ? parts.join('; ') : null;
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .map(([key, fieldValue]) => {
        const fieldMessage = toErrorMessage(fieldValue);
        if (!fieldMessage) return null;
        if (key === 'non_field_errors' || key === '__all__') return fieldMessage;
        return `${key}: ${fieldMessage}`;
      })
      .filter((item): item is string => Boolean(item));
    return entries.length ? entries.join(' | ') : null;
  }

  return null;
};

const summarizeError = (error: unknown): string => {
  const err = error as any;
  if (typeof err?.data?.error === 'string') return err.data.error;
  if (typeof err?.data?.detail === 'string') return err.data.detail;

  const nested = toErrorMessage(err?.data);
  if (nested) return nested;

  if (typeof err?.error === 'string') return err.error;
  return 'Request failed. Please verify filters and try again.';
};

export const ExportToPathReportModal: React.FC<ExportToPathReportModalProps> = ({
  isOpen,
  onClose,
  hasAppliedFilters,
  sourcePathIds,
}) => {
  const navigate = useNavigate();
  const { data: watchlists = [], isLoading: isLoadingWatchlists } = useGetWatchlistsQuery();
  const [createWatchlist] = useCreateWatchlistMutation();
  const [translateToWatchlist, { isLoading: isExporting }] =
    useTranslateKatanaPathsToWatchlistMutation();

  const [selectedWatchlistId, setSelectedWatchlistId] = useState<number | null>(null);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [isCreatingWatchlist, setIsCreatingWatchlist] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [summary, setSummary] =
    useState<KatanaTranslateToWatchlistResponse | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setErrorMessage(null);
    setSummary(null);
    setNewWatchlistName('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (selectedWatchlistId !== null) return;
    if (watchlists.length === 0) return;

    const firstId = Number(watchlists[0]?.id);
    if (Number.isFinite(firstId)) {
      setSelectedWatchlistId(firstId);
    }
  }, [isOpen, selectedWatchlistId, watchlists]);

  const watchlistOptions = useMemo(
    () =>
      watchlists
        .filter((item: any) => Number.isFinite(Number(item?.id)))
        .map((item: any) => ({
          id: Number(item.id),
          name: String(item.name ?? `Watchlist ${item.id}`),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [watchlists],
  );

  const canExport =
    hasAppliedFilters &&
    sourcePathIds.length > 0 &&
    selectedWatchlistId !== null &&
    !isExporting &&
    !isCreatingWatchlist;

  const handleCreateWatchlist = async () => {
    const name = newWatchlistName.trim();
    if (!name) return;

    setErrorMessage(null);
    setIsCreatingWatchlist(true);
    try {
      const created = await createWatchlist({ name }).unwrap();
      if (created?.id) {
        setSelectedWatchlistId(created.id);
      }
      setNewWatchlistName('');
    } catch (error) {
      setErrorMessage(summarizeError(error));
    } finally {
      setIsCreatingWatchlist(false);
    }
  };

  const handleExport = async () => {
    if (!canExport || selectedWatchlistId === null) return;

    setErrorMessage(null);
    setSummary(null);

    try {
      const result = await translateToWatchlist({
        watchlist_id: selectedWatchlistId,
        persist: true,
        ids: sourcePathIds,
      }).unwrap();

      setSummary(result);
    } catch (error) {
      setErrorMessage(summarizeError(error));
    }
  };

  const handleOpenPathReport = () => {
    if (selectedWatchlistId === null) return;

    const searchParams = new URLSearchParams();
    searchParams.append('watchlist_ids', String(selectedWatchlistId));
    navigate(`/paths/report?${searchParams.toString()}`);
    onClose();
  };

  return (
    <ReusableModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      header={<span>Export to Path Report</span>}
      footer={
        <div className="d-flex justify-content-between w-100">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Close
          </button>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleExport}
              disabled={!canExport}
            >
              {isExporting ? 'Exporting...' : 'Export to Watchlist'}
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleOpenPathReport}
              disabled={selectedWatchlistId === null || !summary}
            >
              Open in Path Report
            </button>
          </div>
        </div>
      }
    >
      <div className="d-flex flex-column gap-3">
        <div className="text-muted small">
          Export translates the paths currently shown in the results table,
          saves resolved paths to a watchlist, then lets you open Path Report
          filtered to that watchlist.
        </div>

        <div className="small text-muted">
          Displayed paths selected for export: {sourcePathIds.length}
        </div>

        {!hasAppliedFilters && (
          <div className="alert alert-warning mb-0">
            Apply Katana filters first, then run export.
          </div>
        )}

        {hasAppliedFilters && sourcePathIds.length === 0 && (
          <div className="alert alert-warning mb-0">
            No displayed paths are available to export.
          </div>
        )}

        <div className="row g-2">
          <div className="col-12 col-md-8">
            <label className="form-label mb-1">Target Watchlist</label>
            <select
              className="form-select"
              value={selectedWatchlistId ?? ''}
              disabled={isLoadingWatchlists || isExporting || isCreatingWatchlist}
              onChange={(evt) =>
                setSelectedWatchlistId(
                  evt.target.value ? Number(evt.target.value) : null,
                )
              }
            >
              <option value="">Select watchlist</option>
              {watchlistOptions.map((watchlist) => (
                <option key={watchlist.id} value={watchlist.id}>
                  {watchlist.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-8">
            <label className="form-label mb-1">Create New Watchlist</label>
            <input
              className="form-control"
              type="text"
              placeholder="Watchlist name"
              value={newWatchlistName}
              onChange={(evt) => setNewWatchlistName(evt.target.value)}
              disabled={isCreatingWatchlist || isExporting}
            />
          </div>
          <div className="col-12 col-md-4">
            <button
              type="button"
              className="btn btn-outline-primary w-100"
              onClick={handleCreateWatchlist}
              disabled={!newWatchlistName.trim() || isCreatingWatchlist || isExporting}
            >
              {isCreatingWatchlist ? 'Creating...' : 'Create Watchlist'}
            </button>
          </div>
        </div>

        {errorMessage && <div className="alert alert-danger mb-0">{errorMessage}</div>}

        {summary && (
          <div className="border rounded p-3">
            {(() => {
              const inputSourcePathIdsCount =
                summary.input_ids_count ?? sourcePathIds.length;
              const candidatePaths = summary.candidate_paths ?? 0;
              const successfulTranslations = summary.successful_translations ?? 0;
              const unsuccessfulTranslations = summary.unsuccessful_translations ?? 0;
              const persistedPaths = summary.persisted_paths ?? 0;
              const addedToWatchlist = summary.added_to_watchlist ?? 0;

              return (
                <>
            <div className="fw-semibold mb-2">Export Summary</div>

            <div className="row g-2 mb-3">
              <div className="col-6 col-md-4">
                <div className="small text-muted">Displayed Path IDs</div>
                <div>{sourcePathIds.length.toLocaleString()}</div>
              </div>
              <div className="col-6 col-md-4">
                <div className="small text-muted">Input Source Path IDs</div>
                <div>{inputSourcePathIdsCount.toLocaleString()}</div>
              </div>
            </div>

            {typeof summary.input_ids_count === 'number' &&
              summary.input_ids_count !== sourcePathIds.length && (
                <div className="alert alert-warning py-2 mb-3">
                  Export used {summary.input_ids_count.toLocaleString()} input
                  source path IDs, but {sourcePathIds.length.toLocaleString()} were displayed
                  when export was triggered.
                </div>
              )}

            {candidatePaths !== inputSourcePathIdsCount && (
              <div className="alert alert-info py-2 mb-3">
                Candidate paths ({candidatePaths.toLocaleString()}) are lower than input
                IDs ({inputSourcePathIdsCount.toLocaleString()}) after server-side
                intersection/validation.
              </div>
            )}

            <div className="row g-2 mb-2">
              <div className="col-6 col-md-3">
                <div className="small text-muted">Candidates</div>
                <div>{candidatePaths.toLocaleString()}</div>
              </div>
              <div className="col-6 col-md-3">
                <div className="small text-muted">Successful</div>
                <div>{successfulTranslations.toLocaleString()}</div>
              </div>
              <div className="col-6 col-md-3">
                <div className="small text-muted">Unsuccessful</div>
                <div>{unsuccessfulTranslations.toLocaleString()}</div>
              </div>
              <div className="col-6 col-md-3">
                <div className="small text-muted">Persisted</div>
                <div>{persistedPaths.toLocaleString()}</div>
              </div>
              <div className="col-6 col-md-3">
                <div className="small text-muted">Added</div>
                <div>{addedToWatchlist.toLocaleString()}</div>
              </div>
            </div>

            {Object.keys(summary.unresolved_by_status ?? {}).length > 0 && (
              <div>
                <div className="small text-muted mb-1">Unresolved by status</div>
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(summary.unresolved_by_status).map(([status, count]) => (
                    <span key={status} className="badge text-bg-light border">
                      {status}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    </ReusableModal>
  );
};
